import React, { useState } from 'react';
import {
  Dialog,
  Box,
  Typography,
  Rating,
  Chip,
  Button,
  IconButton,
  Grid,
  Snackbar,
  Alert,
} from '@mui/material';
import { Close, LocationOn, Language, Share } from '@mui/icons-material';
import { Favorite, FavoriteBorder } from '@mui/icons-material';
import { Event } from '../types';
import { useFavorites } from '../contexts/FavoritesContext';
import { eventAPI } from '../services/api';
import MapView from './MapView';

interface EventLightboxProps {
  event: Event | null;
  open: boolean;
  onClose: () => void;
}

const EventLightbox: React.FC<EventLightboxProps> = ({ event, open, onClose }) => {
  const { addToFavorites, removeFromFavorites, isFavorite } = useFavorites();
  const [shareSnackbarOpen, setShareSnackbarOpen] = useState(false);
  const [saveSnackbarOpen, setSaveSnackbarOpen] = useState(false);
  
  if (!event) return null;

  const isEventFavorite = isFavorite(event.uniqueId);

  const handleFavoriteClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (isEventFavorite) {
      removeFromFavorites(event.uniqueId);
    } else {
      addToFavorites(event);
      setSaveSnackbarOpen(true);
    }
  };

  const handleBookNow = async () => {
    try {
      await eventAPI.incrementClick(event.uniqueId);
      window.open(event.affiliateUrl, '_blank', 'noopener,noreferrer');
    } catch (error) {
      console.error('Error tracking click:', error);
      window.open(event.affiliateUrl, '_blank', 'noopener,noreferrer');
    }
  };

  const handleShare = async () => {
    const shareData = {
      title: event.eventTitle,
      text: event.subhead || `Experience ${event.eventTitle}`,
      url: event.affiliateUrl,
    };

    // Check if device supports native sharing
    if (navigator.share && /Android|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent)) {
      try {
        await navigator.share(shareData);
      } catch (error) {
        console.error('Error sharing:', error);
      }
    } else {
      // Desktop: Copy to clipboard
      try {
        await navigator.clipboard.writeText(event.affiliateUrl);
        setShareSnackbarOpen(true);
      } catch (error) {
        console.error('Error copying to clipboard:', error);
        // Fallback for older browsers
        const textArea = document.createElement('textarea');
        textArea.value = event.affiliateUrl;
        document.body.appendChild(textArea);
        textArea.select();
        document.execCommand('copy');
        document.body.removeChild(textArea);
        setShareSnackbarOpen(true);
      }
    }
  };

  return (
    <Dialog 
      open={open} 
      onClose={onClose}
      maxWidth={false}
      PaperProps={{
        sx: {
          width: '90vw',
          height: '80vh',
          maxWidth: '1200px',
          maxHeight: '800px',
          m: 2,
          borderRadius: 1,
          overflow: 'hidden',
        }
      }}
    >
      <Box sx={{ position: 'relative', height: '100%', display: 'flex' }}>
        {/* Close Button */}
        <IconButton
          onClick={onClose}
          sx={{
            position: 'absolute',
            top: 16,
            right: 16,
            zIndex: 1,
            backgroundColor: 'rgba(255, 255, 255, 0.9)',
            '&:hover': {
              backgroundColor: 'rgba(255, 255, 255, 1)',
            }
          }}
        >
          <Close />
        </IconButton>

        {/* Left Side - Image */}
        <Box
          sx={{
            width: { xs: '100%', md: '60%' },
            height: '100%',
            backgroundImage: `url(${event.imageUrl})`,
            backgroundSize: 'cover',
            backgroundPosition: 'center',
            position: 'relative',
          }}
        />

        {/* Right Side - Details */}
        <Box
          sx={{
            width: { xs: '100%', md: '40%' },
            height: '100%',
            p: 3,
            overflowY: 'auto',
            display: { xs: 'none', md: 'block' },
          }}
        >
          <Box sx={{ mb: 2 }}>
            <Typography variant="h4" component="h1" sx={{ fontWeight: 600, mb: 1 }}>
              {event.eventTitle}
            </Typography>
            
            {event.price && (
              <Typography 
                variant="h5" 
                sx={{ 
                  color: '#059669',
                  fontWeight: 700,
                  mb: 2,
                }}
              >
                {event.price}
              </Typography>
            )}

            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
              <Rating value={event.starRating} readOnly size="large" />
              <Typography variant="body1" sx={{ fontWeight: 500 }}>
                ({event.starRating})
              </Typography>
            </Box>

            {event.category && (
              <Chip
                label={
                  event.category === 'hotel' ? 'Hotel' : 
                  event.category === 'vacation_rentals' ? 'Vacation Rental' :
                  event.category === 'activities' ? 'Activities' : 
                  event.category === 'event' ? 'Event' :
                  event.category
                }
                sx={{
                  bgcolor: event.category === 'hotel' || event.category === 'vacation_rentals' ? '#6366f1' : 
                           event.category === 'activities' ? '#0ea5e9' : '#f59e0b',
                  color: 'white',
                  fontWeight: 600,
                  mb: 2,
                }}
              />
            )}
          </Box>

          {event.subhead && (
            <Typography 
              variant="body1" 
              sx={{ 
                color: 'text.secondary',
                lineHeight: 1.6,
                mb: 3,
              }}
            >
              {event.subhead}
            </Typography>
          )}

          {event.address?.street && (
            <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1, mb: 3 }}>
              <LocationOn sx={{ color: 'text.secondary', mt: 0.5, fontSize: '1.2rem' }} />
              <Box>
                <Typography variant="body2" color="text.secondary">
                  {event.address.street}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {event.address.city}, {event.address.state} {event.address.zip}
                </Typography>
              </Box>
            </Box>
          )}

          {/* Map View */}
          {event.address?.latitude && event.address?.longitude && (
            <Box sx={{ mb: 3 }}>
              <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
                Location
              </Typography>
              <Box sx={{ 
                height: 200, 
                borderRadius: 2, 
                overflow: 'hidden',
                border: '1px solid rgba(0, 0, 0, 0.12)'
              }}>
                <MapView 
                  events={[event]}
                  selectedEvent={event}
                  locationName={`${event.address.city}, ${event.address.state}`}
                />
              </Box>
            </Box>
          )}

          {/* Action Buttons */}
          <Box sx={{ mt: 'auto', pt: 2 }}>
            <Grid container spacing={1}>
              <Grid item xs={6}>
                <Button
                  fullWidth
                  variant="contained"
                  onClick={handleBookNow}
                  startIcon={<Language />}
                  sx={{
                    background: 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)',
                    fontWeight: 600,
                    fontSize: '0.75rem',
                    py: 1,
                    height: '40px', // Match height with other buttons
                    borderRadius: 1.5, // Consistent border radius
                    '&:hover': {
                      background: 'linear-gradient(135deg, #4f46e5 0%, #7c3aed 100%)',
                    }
                  }}
                >
                  Book Now
                </Button>
              </Grid>
              <Grid item xs={3}>
                <Button
                  fullWidth
                  variant={isEventFavorite ? 'contained' : 'outlined'}
                  onClick={handleFavoriteClick}
                  sx={{
                    background: isEventFavorite ? '#ef4444' : 'transparent',
                    color: isEventFavorite ? 'white' : '#ef4444',
                    borderColor: '#ef4444',
                    '&:hover': {
                      background: isEventFavorite ? '#dc2626' : 'rgba(239, 68, 68, 0.1)',
                      borderColor: '#ef4444',
                    },
                    fontSize: '0.75rem',
                    py: 1,
                    height: '40px', // Match height with other buttons
                    borderRadius: 1.5, // Consistent border radius
                    minWidth: 'auto',
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                  }}
                >
                  {isEventFavorite ? <Favorite /> : <FavoriteBorder />}
                </Button>
              </Grid>
              <Grid item xs={3}>
                <Button
                  fullWidth
                  variant="outlined"
                  onClick={handleShare}
                  sx={{
                    color: '#6366f1',
                    borderColor: '#6366f1',
                    '&:hover': {
                      background: 'rgba(99, 102, 241, 0.1)',
                      borderColor: '#4f46e5',
                    },
                    fontSize: '0.75rem',
                    py: 1,
                    height: '40px', // Match height with other buttons
                    borderRadius: 1.5, // Consistent border radius
                    minWidth: 'auto',
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                  }}
                >
                  <Share />
                </Button>
              </Grid>
            </Grid>
          </Box>
        </Box>

        {/* Mobile Details Overlay */}
        <Box
          sx={{
            position: 'absolute',
            bottom: 0,
            left: 0,
            right: 0,
            maxHeight: '50vh',
            overflowY: 'auto',
            background: 'linear-gradient(to top, rgba(0,0,0,0.9), rgba(0,0,0,0.7))',
            p: 3,
            color: 'white',
            display: { xs: 'block', md: 'none' },
          }}
        >
          <Typography variant="h5" sx={{ fontWeight: 600, mb: 1 }}>
            {event.eventTitle}
          </Typography>
          
          {event.price && (
            <Typography variant="h6" sx={{ color: '#10b981', fontWeight: 700, mb: 1 }}>
              {event.price}
            </Typography>
          )}

          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
            <Rating value={event.starRating} readOnly size="small" />
            <Typography variant="body2">({event.starRating})</Typography>
          </Box>

          {event.subhead && (
            <Typography variant="body2" sx={{ mb: 2, lineHeight: 1.5 }}>
              {event.subhead}
            </Typography>
          )}

          {event.address?.street && (
            <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1, mb: 2 }}>
              <Typography variant="body2" sx={{ opacity: 0.9 }}>
                📍 {event.address.street}, {event.address.city} {event.address.state} {event.address.zip}
              </Typography>
            </Box>
          )}

          <Grid container spacing={1}>
            <Grid item xs={8}>
              <Button
                fullWidth
                variant="contained"
                onClick={handleBookNow}
                sx={{
                  background: 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)',
                  fontWeight: 600,
                  fontSize: '0.7rem',
                  height: '36px', // Match height with other buttons
                  borderRadius: 1.5, // Consistent border radius
                }}
                size="small"
              >
                Book Now
              </Button>
            </Grid>
            <Grid item xs={2}>
              <Button
                fullWidth
                variant="outlined"
                onClick={handleFavoriteClick}
                sx={{
                  color: 'white',
                  borderColor: 'rgba(255,255,255,0.5)',
                  '&:hover': {
                    borderColor: 'white',
                    backgroundColor: 'rgba(255,255,255,0.1)',
                  },
                  fontSize: '0.7rem',
                  minWidth: 'auto',
                  px: 0.5,
                  height: '36px', // Match height with other buttons
                  borderRadius: 1.5, // Consistent border radius
                }}
                size="small"
              >
                {isEventFavorite ? <Favorite /> : <FavoriteBorder />}
              </Button>
            </Grid>
            <Grid item xs={2}>
              <Button
                fullWidth
                variant="outlined"
                onClick={handleShare}
                sx={{
                  color: 'white',
                  borderColor: 'rgba(255,255,255,0.5)',
                  '&:hover': {
                    borderColor: 'white',
                    backgroundColor: 'rgba(255,255,255,0.1)',
                  },
                  fontSize: '0.7rem',
                  minWidth: 'auto',
                  px: 0.5,
                  height: '36px', // Match height with other buttons
                  borderRadius: 1.5, // Consistent border radius
                }}
                size="small"
              >
                <Share />
              </Button>
            </Grid>
          </Grid>
        </Box>
      </Box>
      
      <Snackbar
        open={shareSnackbarOpen}
        autoHideDuration={3000}
        onClose={() => setShareSnackbarOpen(false)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert 
          onClose={() => setShareSnackbarOpen(false)} 
          severity="success" 
          sx={{ width: '100%' }}
        >
          Link copied to clipboard!
        </Alert>
      </Snackbar>
      
      <Snackbar
        open={saveSnackbarOpen}
        autoHideDuration={3000}
        onClose={() => setSaveSnackbarOpen(false)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert 
          onClose={() => setSaveSnackbarOpen(false)} 
          severity="success" 
          sx={{ width: '100%' }}
        >
          Saved to favorites!
        </Alert>
      </Snackbar>
    </Dialog>
  );
};

export default EventLightbox;